/**
 * <book-preview> — leichtgewichtige, haptische PDF-Buchvorschau.
 *
 * Zeigt einen Button. Beim Klick öffnet sich ein zentriertes Overlay mit
 * einer echten Blätter-Optik (Seite per Ecke ziehen / wischen, Doppelseite am
 * Desktop, Einzelseite mobil). Die schweren Libraries (PDF.js + StPageFlip)
 * werden bewusst ERST beim ersten Öffnen geladen, damit die Seite schnell bleibt.
 *
 * Pflege: Die PDF wird über die Block-Einstellung gesetzt (Datei-URL aus
 * Shopify → Inhalte → Dateien). Sonst ist hier nichts zu konfigurieren.
 *
 * Wird als globales Script via snippets/scripts.liquid eingebunden.
 */

/** @type {Map<string, Promise<void>>} */
const SCRIPT_CACHE = new Map();

/**
 * Lädt ein externes Script genau einmal (Promise wird gecached).
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScriptOnce(src) {
  const cached = SCRIPT_CACHE.get(src);
  if (cached) return cached;

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Konnte Script nicht laden: ${src}`));
    document.head.appendChild(script);
  });

  SCRIPT_CACHE.set(src, promise);
  return promise;
}

/** Kurzes haptisches Feedback auf unterstützten Geräten. */
function haptic(ms = 8) {
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* ignore */
  }
}

class BookPreview extends HTMLElement {
  /** @type {HTMLElement | null} */
  trigger = null;
  /** @type {HTMLElement | null} */
  overlay = null;
  /** @type {any} StPageFlip-Instanz */
  pageFlip = null;
  /** @type {boolean} */
  isOpen = false;
  /** @type {Promise<void> | null} Promise des laufenden Lib-Ladens */
  _libsPromise = null;

  connectedCallback() {
    this.trigger = this.querySelector('[data-book-preview-trigger]');
    this._onTriggerClick = (event) => {
      event.preventDefault();
      this.open();
    };
    this.trigger?.addEventListener('click', this._onTriggerClick);

    this._onKeydown = (event) => this._handleKeydown(event);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this._onTriggerClick);
    this._teardown();
  }

  get pdfUrl() {
    return this.dataset.pdfUrl?.trim() || '';
  }

  get title() {
    return this.dataset.title?.trim() || '';
  }

  get maxPages() {
    const value = parseInt(this.dataset.maxPages || '0', 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * Lädt PDF.js + StPageFlip (einmalig) und richtet den PDF-Worker ein.
   * Der Worker wird als same-origin Blob geladen, um Cross-Origin-Worker-
   * Beschränkungen (Asset liegt auf cdn.shopify.com) sicher zu umgehen.
   */
  async _ensureLibs() {
    if (this._libsPromise) return this._libsPromise;

    this._libsPromise = (async () => {
      await Promise.all([
        loadScriptOnce(this.dataset.pdfjsSrc || ''),
        loadScriptOnce(this.dataset.pageflipSrc || ''),
      ]);

      const pdfjsLib = /** @type {any} */ (window).pdfjsLib;
      if (!pdfjsLib) throw new Error('PDF.js wurde nicht geladen.');

      // Worker als Blob → garantiert same-origin, kein CORS-Worker-Problem.
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        const workerSrc = this.dataset.pdfWorkerSrc || '';
        try {
          const res = await fetch(workerSrc);
          const blob = await res.blob();
          pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
        } catch {
          // Fallback: direkte URL (PDF.js wrappt cross-origin selbst per importScripts)
          pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
        }
      }
    })();

    return this._libsPromise;
  }

  async open() {
    if (this.isOpen) return;
    if (!this.pdfUrl) {
      console.warn('[book-preview] Keine PDF hinterlegt.');
      return;
    }

    this.isOpen = true;
    this._buildOverlay();
    this._lockScroll();
    document.addEventListener('keydown', this._onKeydown);

    // Enter-Animation
    requestAnimationFrame(() => this.overlay?.classList.add('is-open'));

    try {
      await this._ensureLibs();
      await this._renderBook();
    } catch (error) {
      console.error('[book-preview]', error);
      this._showError(this._errorMessage(error));
    }
  }

  /**
   * Übersetzt einen PDF.js-/Netzwerkfehler in eine verständliche Meldung.
   * @param {any} error
   * @returns {string}
   */
  _errorMessage(error) {
    const name = error?.name || '';
    const msg = String(error?.message || '');

    // CORS oder Netzwerk: fetch wirft TypeError "Failed to fetch".
    if (name === 'TypeError' || /failed to fetch|load failed|networkerror/i.test(msg)) {
      return 'Die PDF konnte nicht geladen werden. Der externe Server erlaubt vermutlich keinen Zugriff von dieser Seite (CORS) oder der Link zeigt nicht direkt auf die Datei.';
    }
    if (name === 'MissingPDFException' || /missing pdf|404/i.test(msg)) {
      return 'Die PDF wurde unter diesem Link nicht gefunden (404). Bitte den Link prüfen.';
    }
    if (name === 'InvalidPDFException' || /invalid pdf/i.test(msg)) {
      return 'Der Link liefert keine gültige PDF (möglicherweise eine Vorschau-/Viewer-Seite statt der Datei selbst).';
    }
    if (name === 'UnexpectedResponseException') {
      return 'Unerwartete Antwort vom Server der PDF. Der Link erlaubt evtl. keinen direkten Zugriff (CORS).';
    }
    return 'Die Vorschau konnte nicht geladen werden.';
  }

  close() {
    if (!this.isOpen) return;
    this.overlay?.classList.remove('is-open');

    const overlay = this.overlay;
    const finish = () => {
      this._teardown();
      this.trigger?.focus?.();
    };
    // Auf Transition warten, dann aufräumen (mit Fallback-Timeout).
    if (overlay) {
      let done = false;
      const onEnd = () => {
        if (done) return;
        done = true;
        finish();
      };
      overlay.addEventListener('transitionend', onEnd, { once: true });
      setTimeout(onEnd, 400);
    } else {
      finish();
    }
  }

  _teardown() {
    document.removeEventListener('keydown', this._onKeydown);
    try {
      this.pageFlip?.destroy();
    } catch {
      /* ignore */
    }
    this.pageFlip = null;
    this.overlay?.remove();
    this.overlay = null;
    this._unlockScroll();
    this.isOpen = false;
  }

  _handleKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'ArrowRight') {
      this.pageFlip?.flipNext();
    } else if (event.key === 'ArrowLeft') {
      this.pageFlip?.flipPrev();
    }
  }

  _lockScroll() {
    this._scrollY = window.scrollY;
    document.documentElement.style.setProperty('overflow', 'hidden');
    document.body.style.setProperty('overflow', 'hidden');
  }

  _unlockScroll() {
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
  }

  _buildOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'book-preview-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    if (this.title) overlay.setAttribute('aria-label', this.title);

    overlay.innerHTML = `
      <div class="book-preview-overlay__backdrop" data-bp-close></div>
      <div class="book-preview-modal" role="document">
        <header class="book-preview-modal__bar">
          <span class="book-preview-modal__title">${this._escape(this.title)}</span>
          <button type="button" class="book-preview-modal__close" data-bp-close aria-label="Schließen">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </header>

        <div class="book-preview-modal__stage">
          <button type="button" class="book-preview-nav book-preview-nav--prev" data-bp-prev aria-label="Vorherige Seite" hidden>
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>

          <div class="book-preview-book" data-bp-book></div>

          <button type="button" class="book-preview-nav book-preview-nav--next" data-bp-next aria-label="Nächste Seite" hidden>
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>

          <div class="book-preview-loader" data-bp-loader>
            <span class="book-preview-loader__spinner" aria-hidden="true"></span>
            <span class="book-preview-loader__text" data-bp-loader-text>Vorschau wird geladen…</span>
          </div>
        </div>

        <footer class="book-preview-modal__foot">
          <span class="book-preview-modal__counter" data-bp-counter></span>
        </footer>
      </div>
    `;

    overlay.querySelectorAll('[data-bp-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    overlay.querySelector('[data-bp-prev]')?.addEventListener('click', () => this.pageFlip?.flipPrev());
    overlay.querySelector('[data-bp-next]')?.addEventListener('click', () => this.pageFlip?.flipNext());

    document.body.appendChild(overlay);
    this.overlay = overlay;
  }

  async _renderBook() {
    const pdfjsLib = /** @type {any} */ (window).pdfjsLib;
    const StPageFlip = /** @type {any} */ (window).St;
    const bookEl = this.overlay?.querySelector('[data-bp-book]');
    const loaderText = this.overlay?.querySelector('[data-bp-loader-text]');
    if (!bookEl) return;

    // disableAutoFetch: nur die tatsächlich gezeigten Seiten per Range-Request
    // laden statt der ganzen Datei (wichtig bei großen PDFs; Server muss
    // 'accept-ranges: bytes' unterstützen).
    const pdf = await pdfjsLib.getDocument({
      url: this.pdfUrl,
      disableAutoFetch: true,
      disableStream: false,
    }).promise;
    const total = this.maxPages ? Math.min(this.maxPages, pdf.numPages) : pdf.numPages;

    // Seitenverhältnis aus Seite 1 ableiten.
    const firstPage = await pdf.getPage(1);
    const baseViewport = firstPage.getViewport({ scale: 1 });
    const ratio = baseViewport.width / baseViewport.height; // Breite / Höhe

    // Alle Seiten als gestochen scharfe Bilder rendern.
    const TARGET_PX = 1100; // Renderbreite pro Seite
    const pageElements = [];
    for (let num = 1; num <= total; num++) {
      if (loaderText) loaderText.textContent = `Seite ${num} / ${total}…`;
      const page = num === 1 ? firstPage : await pdf.getPage(num);
      const scale = TARGET_PX / page.getViewport({ scale: 1 }).width;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport }).promise;

      const pageEl = document.createElement('div');
      pageEl.className = 'book-preview-page';
      const img = document.createElement('img');
      img.src = canvas.toDataURL('image/jpeg', 0.85);
      img.alt = `Seite ${num}`;
      img.draggable = false;
      pageEl.appendChild(img);
      bookEl.appendChild(pageEl);
      pageElements.push(pageEl);

      // Canvas freigeben.
      canvas.width = canvas.height = 0;
    }

    // Buchgröße aus Verhältnis ableiten (StPageFlip skaliert per 'stretch').
    // Alle Maße beziehen sich auf EINE Seite; Doppelseite = 2× Breite.
    const baseW = 480;
    const baseH = Math.round(baseW / ratio);

    this.pageFlip = new StPageFlip.PageFlip(bookEl, {
      width: baseW,
      height: baseH,
      size: 'stretch',
      minWidth: 300,
      maxWidth: 700,
      minHeight: Math.round(300 / ratio),
      maxHeight: Math.round(700 / ratio),
      drawShadow: true,
      maxShadowOpacity: 0.5,
      flippingTime: 700,
      usePortrait: true,
      showCover: true,
      mobileScrollSupport: false,
      swipeDistance: 30,
      clickEventForward: false,
    });

    this.pageFlip.loadFromHTML(pageElements);

    // Loader ausblenden, Buch zeigen.
    this.overlay?.querySelector('[data-bp-loader]')?.setAttribute('hidden', '');
    this.overlay?.classList.add('is-ready');

    const updateUi = () => this._updateUi(total);
    this.pageFlip.on('flip', () => {
      haptic(8);
      updateUi();
    });
    this.pageFlip.on('changeOrientation', updateUi);

    // Navigations-Pfeile sichtbar machen + Zähler initialisieren.
    this.overlay?.querySelectorAll('.book-preview-nav').forEach((el) => el.removeAttribute('hidden'));
    updateUi();
  }

  _updateUi(total) {
    if (!this.pageFlip || !this.overlay) return;
    const current = this.pageFlip.getCurrentPageIndex(); // 0-basiert
    const counter = this.overlay.querySelector('[data-bp-counter]');
    if (counter) counter.textContent = `${current + 1} / ${total}`;

    const prev = this.overlay.querySelector('[data-bp-prev]');
    const next = this.overlay.querySelector('[data-bp-next]');
    prev?.toggleAttribute('disabled', current <= 0);
    next?.toggleAttribute('disabled', current >= total - 1);
  }

  _showError(message = 'Die Vorschau konnte nicht geladen werden.') {
    const stage = this.overlay?.querySelector('.book-preview-modal__stage');
    if (!stage) return;
    stage.innerHTML = `
      <div class="book-preview-error">
        <p>${this._escape(message)}</p>
        <button type="button" class="book-preview-error__close" data-bp-close>Schließen</button>
      </div>
    `;
    stage.querySelector('[data-bp-close]')?.addEventListener('click', () => this.close());
  }

  _escape(str) {
    return String(str).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }
}

if (!customElements.get('book-preview')) {
  customElements.define('book-preview', BookPreview);
}
